import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CalendarClock } from "lucide-react";
import { site } from "@/config";
import { FadeUp, MaskedLine, MonoLabel } from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";

const stats = [
  {
    metric: "Top 3",
    label: "on Google for their main keyword, up from page two",
  },
  {
    metric: "7×",
    label: "more often shown by Google than before we started",
  },
  {
    metric: "Page 1",
    label: "for the second big keyword, up from deep on page two",
  },
];

export default function CaseStudy() {
  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" data-testid="case-study-page">
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <nav className="flex items-center justify-between px-6 sm:px-12 h-[72px]">
          <Link
            to="/"
            data-testid="case-study-back-link"
            className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Back
          </Link>
          <a
            href={site.calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="case-study-book-btn"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-xs tracking-[0.15em] uppercase px-5 py-3 rounded-full hover:scale-105 transition-transform duration-300"
          >
            Book a call
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </nav>
      </header>

      <article className="max-w-3xl mx-auto px-6 pt-40 pb-32">
        <FadeUp>
          <MonoLabel>Case study · IT services · Dubai</MonoLabel>
        </FadeUp>
        <h1 className="mt-6 font-display font-semibold text-4xl sm:text-6xl tracking-[-0.03em] leading-[1.05]">
          <MaskedLine>The IT company that</MaskedLine>
          <MaskedLine delay={0.1}>
            <span className="text-gradient">AI now recommends.</span>
          </MaskedLine>
        </h1>
        <FadeUp delay={0.2}>
          <p className="mt-8 text-lg sm:text-xl text-muted-foreground leading-relaxed">
            Six months of work with an IT services company in Dubai. This is
            what we did, and what came out of it.
          </p>
        </FadeUp>

        <FadeUp>
          <div className="mt-16" data-testid="case-chapter-01">
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-[-0.02em]">
              Where they were
            </h2>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              They do good work and their clients stay with them for years. But
              online, they were hard to find. Their main keywords sat on page
              two of Google, and they didn't come up in AI answers at all. Most
              of their business came from referrals, which is great until it
              isn't.
            </p>
          </div>
        </FadeUp>

        <FadeUp>
          <div className="mt-20" data-testid="case-chapter-02">
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-[-0.02em]">
              What we worked on
            </h2>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              We didn't do anything clever. We picked the two services that
              actually bring them business and rebuilt those pages properly.
              Clearer structure, more depth, and real answers to the questions
              their buyers ask. Around that, we published supporting articles
              on the same topics and kept their LinkedIn active.
            </p>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              We also spent time on the boring technical side, making sure
              Google could read the pages easily and understand what the
              company does. That part isn't exciting, but it's usually where
              the difference is made.
            </p>
          </div>
        </FadeUp>

        <FadeUp>
          <div className="mt-20" data-testid="case-chapter-03">
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-[-0.02em]">
              A problem we didn't expect
            </h2>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              A few months in, things stalled. New pages weren't showing up on
              Google no matter what we published. After some digging, we found
              a fault on their server that was quietly stopping Google from
              reading new pages at all. It had nothing to do with the content.
              Once their team fixed it, the pages started moving within weeks.
            </p>
          </div>
        </FadeUp>

        <FadeUp>
          <div className="mt-20" data-testid="case-chapter-04">
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-[-0.02em]">
              Where they ended up
            </h2>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Their main keyword moved from page two into the top three results
              on Google, and it's stayed there through two core updates. The
              second keyword cut its average position in half and reached the
              top of page one. Google now shows their pages five to seven times
              more often than before we started.
            </p>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              And because the pages were written to answer questions clearly,
              Google's AI started quoting them for those searches. Some days it
              names the company directly in the answer, as one of the well
              rated providers in Dubai.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {stats.map((s) => (
                <div
                  key={s.metric}
                  className="rounded-2xl border border-black/10 bg-card p-6"
                  data-testid={`case-stat-${s.metric.replace(/\W+/g, "-").toLowerCase()}`}
                >
                  <span className="font-display font-semibold text-4xl text-gradient">
                    {s.metric}
                  </span>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>

        <FadeUp>
          <div className="mt-20" data-testid="case-chapter-05">
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-[-0.02em]">
              What I'd tell anyone reading this
            </h2>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              The rankings and AI mentions are great, but I want to be straight
              about something: clicks didn't grow at the same rate. People
              often get their answer without visiting a website now, and that's
              just where search is heading. Being quoted still matters, because
              it puts your name in front of buyers early. The next step for
              them is paid ads and the local map pack, so all this visibility
              turns into actual phone calls.
            </p>
          </div>
        </FadeUp>

        <FadeUp>
          <div className="relative overflow-hidden mt-24 rounded-3xl border border-black/10 bg-card px-6 sm:px-12 py-16 text-center">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[50vw] h-[50vw] rounded-full bg-[#0071e3]/[0.08] blur-3xl animate-aurora" />
              <ParticleField className="absolute inset-0 w-full h-full" />
            </div>
            <div className="relative z-10">
              <h2 className="font-display font-semibold text-3xl sm:text-5xl tracking-[-0.03em] leading-[1.05]">
                Want the <span className="text-gradient">full story?</span>
              </h2>
              <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-lg mx-auto">
                I left out the good stuff on purpose: the exact keywords, the
                page structure, the month by month swings. I'm happy to walk
                you through all of it on a call. No pressure, just book a time
                with me for a chat.
              </p>
              <a
                href={site.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="case-study-cta-btn"
                className="group mt-10 inline-flex items-center gap-3 bg-primary text-primary-foreground rounded-full px-10 py-5 text-base font-medium hover:scale-[1.05] hover:shadow-[0_8px_30px_rgba(0,113,227,0.35)] transition-[transform,box-shadow] duration-300"
              >
                <CalendarClock className="w-4 h-4" />
                Book time for a chat
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </div>
        </FadeUp>
      </article>
    </div>
  );
}
